import { theme } from '@theme';
import { Column } from 'react-table';
import { DealType } from '../../../../../enums/DealType';
import { useCheckDevice } from '../../../../../hooks/UseCheckDevice';
import { useDealType } from '../../../../../hooks/UseDealType';
import { PortfolioResource } from '../../../../../types/PortfolioResource';
import LastUpdated from '../../../../shared/react-table-wrapper/columns/last-updated/LastUpdated';
import { Typography } from '../../../../style';
import PortfolioAssigneeFilter from '../../table-header/portfolio-assignee-filter/PortfolioAssigneeFilter';
import PortfolioBpsFilter from '../../table-header/portfolio-bps-filter/PortfolioBpsFilter';
import PortfolioDealFilter from '../../table-header/portfolio-deal-filter/portfolioDealFilter';
import PortfolioDealTypeFilter from '../../table-header/portfolio-deal-type-filter/PortfolioDealTypeFilter';
import PortfolioInMarketFilter from '../../table-header/portfolio-inMarket-filter/portfolioInMarketFilter';
import PortfolioInsightsFilter from '../../table-header/portfolio-insights-filter/PortfolioInsightsFilter';
import PortfolioUpdatedFilter from '../../table-header/portfolio-updated-filter/PortfolioUpdatedFilter';
import PortfolioVehicleFilter from '../../table-header/portfolio-vehicle-filter/portfolioVehicleFilter';
import BuyerCurrentVehicle from '../buyer-current-vehicle/BuyerCurrentVehicle';
import DealTypeColumn from '../deal-type/DealTypeColumn';
import PortfolioNotesAndInsights from '../notes-and-insights-column/PortfolioNotesAndInsights';

const getDealTypeIcon = (
  dealType: number,
  isMobileDevice: boolean,
  getDealTypeNumber: (dealType: string) => number,
): JSX.Element => {
  if (!isMobileDevice) {
    return <DealTypeColumn dealType={dealType} />;
  } else {
    return (
      <>
        {dealType === getDealTypeNumber(DealType.Lease) && (
          <span className='deal-type-sticker Lease'>L</span>
        )}
        {dealType === getDealTypeNumber(DealType.Finance) && (
          <span className='deal-type-sticker Finance'>F</span>
        )}
        {dealType === getDealTypeNumber(DealType.Cash) && (
          <span className='deal-type-sticker Cash'>C</span>
        )}
      </>
    );
  }
};

export const allDealsColumns = (): Column<PortfolioResource>[] => {
  const { getDealTypeNumber } = useDealType();
  const { isMobileDevice } = useCheckDevice();

  const commonColumns = [
    {
      Header: !isMobileDevice ? 'Buyer/Current Vehicle' : 'Customer/Vehicle',
      id: 'customerDetails',
      Cell: ({ row: { original } }) => (
        <BuyerCurrentVehicle
          customer={original.customer}
          currentVehicle={original.currentVehicle}
          closedDeal={original.closedDeal}
          dealerId={original.dealerId}
          vinNumber={original.replacementDeal.vin}
          user={original.user}
        />
      ),
      width: 500,
      minWidth: 200,
      sortAccessor: 'customer',
      isSortable: true,
    },
    {
      Header: !isMobileDevice ? <PortfolioDealTypeFilter /> : '',
      id: 'dealType',
      Cell: ({ row: { original } }) =>
        getDealTypeIcon(original.closedDeal.dealType, isMobileDevice, getDealTypeNumber),
      width: 150,
      minWidth: 150,
      sortAccessor: 'dealType',
      headerClassName: 'portfolio-header-column',
      isSortable: isMobileDevice ? false : true,
      paddingRight: isMobileDevice ? 30 : '',
    },
  ];

  const mobileColumns = [
    {
      Header: 'BPS',
      id: 'bpsScore',
      Cell: ({ row: { original } }) => (
        <Typography size={theme.typography.h2} fontWeight={theme.typography.fontWeights.bold}>
          {original.replacementDeal.bps_Value}
        </Typography>
      ),
      accessor: (d: PortfolioResource) => d.replacementDeal.bps_Value.toString(),
      width: 80,
      minWidth: 55,
      className: 'bps-column',
      headerClassName: 'bps-column-header portfolio-header-column',
      sortAccessor: 'bps',
      isSortable: true,
    },
  ];

  const desktopColumns = [
    {
      Header: <PortfolioAssigneeFilter />,
      id: 'assignee',
      Cell: ({ row: { original } }) => (
        <Typography size={theme.typography.h5} fontWeight={theme.typography.fontWeights.normal}>
          {original.user.fullName}
        </Typography>
      ),
      width: 200,
      minWidth: 110,
      sortAccessor: 'assignee',
      headerClassName: 'portfolio-header-column',
      isSortable: true,
    },
    {
      Header: <PortfolioUpdatedFilter />,
      id: 'lastUpdated',
      Cell: ({ row: { original } }) => (
        <LastUpdated lastUpdatedText=' D' lastUpdated={original.closedDeal.sinceUpdatedDate} />
      ),
      width: 150,
      minWidth: 105,
      sortAccessor: 'updated',
      headerClassName: 'portfolio-header-column',
      isSortable: true,
    },
    {
      Header: <PortfolioInsightsFilter cssClass='insights-filter-all-deals' />,
      id: 'notesAndInsights',
      Cell: ({ row: { original } }) => (
        <PortfolioNotesAndInsights
          dealerId={original.dealerId}
          isBalloon={original.closedDeal.isBalloon}
          isConquest={original.closedDeal.isConquest}
          isPrivateIncentiveApplied={original.replacementDeal.isPrivateIncentiveApplied}
          isFordPrivateIncentive={original.closedDeal.isFmcPrivateOffer}
          hasOpenRecall={original.closedDeal.hasOpenRecall}
          inMarket={original.customer.inMarket}
          recall={original.recall}
          wasMailerSent={original.closedDeal.wasMailerSent}
          closedDealId={original.closedDealId}
          fullName={original.customer.fullName}
          vin={original.replacementDeal.vin}
          status={original.closedDeal.statusDisplayName}
          subStatus={original.closedDeal.subStatusDisplayName}
          isLeaseBuyout={original.closedDeal.isLeaseBuyout}
          expireDate={original.privateIncentiveExpiryDates}
          privateIncentiveAmount={original.privateIncentiveAmount}
          isEvEnabled={
            original.closedDeal.evPropensityScore >= 81 &&
            original.closedDeal.evPropensityScore <= 100
          }
          lastName={original.customer.name_last}
          isCpo={original.closedDeal.isCpo}
          shopperActivity={original.shopperActivity}
        />
      ),
      width: 300,
      minWidth: 220,
      headerClassName: 'notes-and-insights-header',
    },
    {
      Header: <PortfolioBpsFilter />,
      id: 'bpsScore',
      Cell: ({ row: { original } }) => (
        <Typography size={theme.typography.h2} fontWeight={theme.typography.fontWeights.bold}>
          {original.replacementDeal.bps_Value}
        </Typography>
      ),
      accessor: (d: PortfolioResource) => d.replacementDeal.bps_Value.toString(),
      width: 80,
      minWidth: 55,
      className: 'bps-column',
      headerClassName: 'bps-column-header portfolio-header-column',
      sortAccessor: 'bps',
      isSortable: true,
    },
    {
      Header: <PortfolioInMarketFilter />,
      id: 'inMarketScore',
      Cell: ({ row: { original } }) => (
        <Typography size={theme.typography.h5} fontWeight={theme.typography.fontWeights.normal}>
          {original.replacementDeal.inMarketScore}
        </Typography>
      ),
      accessor: (d: PortfolioResource) => d.replacementDeal?.inMarketScore?.toString(),
      width: 100,
      minWidth: 85,
      sortAccessor: 'inMarketScore',
      isSortable: true,
    },
    {
      Header: <PortfolioVehicleFilter />,
      id: 'vehicleScore',
      Cell: ({ row: { original } }) => (
        <Typography size={theme.typography.h5} fontWeight={theme.typography.fontWeights.normal}>
          {original.replacementDeal.vehicleScore}
        </Typography>
      ),
      width: 100,
      minWidth: 55,
      sortAccessor: 'vehicleScore',
      isSortable: true,
    },
    {
      Header: <PortfolioDealFilter />,
      id: 'dealScore',
      Cell: ({ row: { original } }) => (
        <Typography size={theme.typography.h5} fontWeight={theme.typography.fontWeights.normal}>
          {original.replacementDeal.dealScore}
        </Typography>
      ),
      width: 100,
      minWidth: 55,
      sortAccessor: 'dealScore',
      isSortable: true,
    },
  ];

  if (!isMobileDevice) {
    return [...commonColumns, ...desktopColumns];
  } else {
    return [...commonColumns, ...mobileColumns];
  }
};
